import React, { useEffect, useState } from 'react';
import { useApp } from '../../AppContext';
import "bootstrap-icons/font/bootstrap-icons.css";
import { Tooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import AppDataTable from '../../components/AppDataTable';
import {
    fetchTestimonials,
    addTestimonial,
    updateTestimonial,
    deleteTestimonial,
    fetchAuthors
} from '../../apiCalls';
import { IMG_BASE_URL } from '../../Helpers';
import { showConfirm } from '../../ConfirmBox';
export default function Testimonials() {
    const { testimonials, setTestimonials, authors, setAuthors } = useApp();
    const [isFormVisible, setIsFormVisible] = useState(false);
    const [editData, setEditData] = useState(null);

    const [formData, setFormData] = useState({
        name: '', message: '', role: '', author_id: ''
    });
    useEffect(() => {
        if (editData) {
            // FIND THE AUTHOR ID BY NAME
            // We search through the authors list to find a name match
            const matchingAuthor = authors.find(
                a => (a.author_name || a.name) === editData.name
            );

            setFormData({
                name: editData.name || '',
                message: editData.message || '',
                role: editData.role || '',
                // Use the ID from the matching author if found
                author_id: matchingAuthor ? matchingAuthor.author_id : ''
            });
        } else {
            setFormData({ name: '', message: '', role: '', author_id: '' });
        }
    }, [editData, authors]);
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const loadTestimonials = async () => {
        try {
            const data = await fetchTestimonials();
            setTestimonials(data);
        } catch (error) {
            console.error("Error fetching testimonials:", error);
        }
    };
    const handleAuthorChange = (e) => {
        const selectedId = e.target.value;
        // Find the author object to get the name for display purposes
        const selectedAuthor = authors.find(a => String(a.author_id) === String(selectedId));

        setFormData(prev => ({
            ...prev,
            author_id: selectedId,
            name: selectedAuthor ? (selectedAuthor.author_name || selectedAuthor.name) : ''
        }));
    };

    const loadAuthorsData = async () => {
        try {
            const data = await fetchAuthors();
            setAuthors(data || []);
        } catch (error) {
            console.error("Error fetching authors:", error);
        }
    };

    useEffect(() => {
        if (!testimonials || testimonials.length === 0) {
            loadTestimonials();
        }
        if (!authors || authors.length === 0) {
            loadAuthorsData();
        }

    }, []);
    const columns = [
        {
            name: "Photo",
            width: "80px",
            cell: (row) => (
                <img
                    // Assuming testimonials folder, if not, adjust the string "testimonials/"
                    src={`${IMG_BASE_URL}authors/${row.image}`}
                    alt="user"
                    style={{
                        width: "50px",
                        height: "50px",
                        objectFit: "cover",
                        borderRadius: "50%",
                        border: "1px solid #ddd"
                    }}
                    // Fallback for broken images
                    onError={(e) => { e.target.src = ""; }}
                />
            ),
        },
        {
            name: "Testimonial By",
            selector: row => row.name, // Matches 'Testimonial By' requirement
            sortable: true,
            width: "200px"
        },
        {
            name: "Role", // Added Role to the Table
            selector: row => row.role || "-",
            sortable: true,
            width: "150px"
        },
        {
            name: "Testimonial",
            selector: row => row.message, // Matches 'Testimonial' requirement
            sortable: true,
            wrap: true, // Useful for long text
        },
        {
            name: "Action",
            width: "120px",
            cell: (row) => (
                <div className="d-flex gap-3 fs-5">
                    <i
                        className="bi bi-pencil text-warning"
                        data-tooltip-id="editTooltip"
                        data-tooltip-content="Edit Testimonial"
                        style={{ cursor: "pointer" }}
                        onClick={() => handleEdit(row)}
                    ></i>

                    <i
                        className="bi bi-trash text-danger"
                        data-tooltip-id="deleteTooltip"
                        data-tooltip-content="Delete Testimonial"
                        style={{ cursor: "pointer" }}
                        onClick={() => handleDelete(row)}
                    ></i>
                </div>
            ),
            ignoreRowClick: true,
        }
    ];
    const handleDelete = (row) => {
        showConfirm(
            "Confirm Delete",
            "Are you sure you want to delete this testimonial? This action cannot be undone.",
            async () => {
                // This block only runs if the user clicks "Yes"
                try {
                    await deleteTestimonial(row.testimonial_id);
                    loadTestimonials(); // Refresh list
                } catch (error) {
                    console.error("Delete failed:", error);
                }
            }
        );
    };


    // --- Handlers ---
    const handleAddClick = () => {
        setEditData(null); // Clear any previous edit data
        setIsFormVisible(true);
    };

    const handleEdit = (row) => {
        setEditData(row); // Pass the row data to the form
        setIsFormVisible(true);
    };

    const handleCloseForm = () => {
        setIsFormVisible(false);
        setEditData(null);
    };
    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation check (though 'required' attribute handles this)
        if (!formData.name || !formData.message || !formData.role) return alert("All fields are mandatory");

        try {
            if (editData) {
                await updateTestimonial(editData.testimonial_id, formData);
            } else {
                await addTestimonial(formData);
            }

            handleCloseForm();
            loadTestimonials();
        } catch (error) {
            console.error(error);
        }
    };

    return (
        <>
            {isFormVisible ? (
                <>
                    <div className='page-title-container'>
                        <h2>{editData ? "Edit Testimonial" : "Add New Testimonial"}</h2>
                        <button className="btn-close" onClick={handleCloseForm}></button>
                    </div>
                    <div className='add-testimonials border p-3 mb-4 mt-4'>
                        <div className="mt-3">
                            <form onSubmit={handleSubmit}>
                                <div className="mb-3">
                                    <label className="form-label">Select Author <span className="text-danger">*</span></label>
                                    <select
                                        className="form-select"
                                        name="name"
                                        value={formData.author_id}
                                        onChange={handleAuthorChange}
                                        required
                                    >
                                        <option value="">-- Choose an Author --</option>
                                        {authors?.map((author, index) => (
                                            <option key={index} value={author.author_id}>
                                                {author.author_name || author.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Role / Designation <span className="text-danger">*</span></label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="role"
                                        value={formData.role}
                                        onChange={handleChange}
                                        placeholder="e.g. എഴുത്തുകാരി, സ്വതന്ത്ര എഴുത്തുകാരൻ"
                                        required
                                    />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Message <span className="text-danger">*</span></label>
                                    <textarea
                                        className="form-control"
                                        name="message"
                                        rows="5"
                                        value={formData.message}
                                        onChange={handleChange}
                                        placeholder="Enter the testimonial message here..."
                                        required
                                    ></textarea>
                                </div>

                                <div className=" gap-2">
                                    <button type="submit" className="btn btn-secondary btn-sm save-btn me-2">
                                        {editData ? "Update" : "Save"} Testimonial
                                    </button>
                                    <button type="button" className="btn btn-sm btn-outline-secondary " onClick={handleCloseForm}>
                                        Cancel
                                    </button>
                                </div>
                            </form>

                        </div>
                    </div>
                </>
            ) : (
                <>
                    <div className='page-title-container'>
                        <h2>Testimonials <span>List of all testimonials</span></h2>
                        <button className='btn btn-secondary btn-sm save-btn' onClick={handleAddClick}>Add Testimonials</button>
                    </div>

                    <div className="table-responsive">
                        <AppDataTable
                            columns={columns}
                            data={testimonials}
                        />
                    </div>
                    <Tooltip id="editTooltip" />
                    <Tooltip id="deleteTooltip" />
                </>

            )}
        </>
    )
}
